/* eslint-disable no-nested-ternary */
import {
  BorrowerDetailsTable, DataSummaryCard, Page, TotalsComparison,
} from '@/components';
import {
  Center,
  Group,
  Loader,
  Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { roundNumber } from '@/helpers/number';
import { formatNumberWithCommas } from '@/helpers/string';
import TableSimple from '@/components/TableSimple';
import { useEffect, useState } from 'react';
import { initialState } from '@/data/features/proposal/proposal';
import { notifications } from '@mantine/notifications';
import {
  useCalculateCycleMortgageRepaymentTableMutation,
  useCalculateCycleMortgageWithSamePeriodMutation,
  useGetOfferByIdMutation,
} from '@/services/api';
import CurrencyNisIcon from '@/assets/images/currencyNis.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './mortgageCycleSummary.module.css';
import { MORTGAGE_CYCLE_ACTIONS } from '@/data/constants';

const MortgageCycleSummary = () => {
  const { t } = useTranslation();
  const [proposal, setProposal] = useState(initialState);
  const [mortgageCycleDetails, setMortgageCycleDetails] = useState(null);
  const suffix = <CurrencyNisIcon style={{ width: '12px' }} />;
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [calculateCycleMortgageRepaymentTable] = useCalculateCycleMortgageRepaymentTableMutation();
  const [calculateCycleMortgageWithSamePeriod] = useCalculateCycleMortgageWithSamePeriodMutation();
  const [apiGetOfferById] = useGetOfferByIdMutation();
  const [error, setError] = useState(false);

  // Calculate totals from mortgage cycle data
  const totalLoanAmount = mortgageCycleDetails?.currentLoans?.reduce((sum, loan) => sum
    + (loan.totalAmount || 0), 0) || 0;

  useEffect(() => {
    const getOfferById = async (offerId) => {
      const { data: result } = await apiGetOfferById({ id: offerId });
      const offer = result?.data ?? null;
      return offer;
    };
    const calculateMortgageCycleSummary = async (offerId) => {
      if (!offerId) {
        setError(true);
      }
      try {
        setLoading(true);
        const offer = await getOfferById(offerId);
        if (!offer) {
          setError(true);
        }
        const {
          cycleMortgage,
          _id,
          propertyValuation,
          loan,
          loanAmount,
          income,
          outcome,
          borrower,
        } = offer;

        const { loans } = cycleMortgage;

        // eslint-disable-next-line max-len
        const isMergingLoans = cycleMortgage.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation || cycleMortgage.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans;

        const { data: repaymentTableData } = await calculateCycleMortgageRepaymentTable(
          {
            updatedLoansForSaveAndCalculation: loans,
            isMergingLoans,
          },
        ).unwrap();

        const { data: totalData } = await calculateCycleMortgageWithSamePeriod(
          {
            loans,
            isMergingLoans,
          },
        ).unwrap();

        const {
          title,
          hebrew: h,
          _id: loanId,
        } = loan;

        const objToState = {
          propertyValuation,
          loanTypeName: title,
          loanTypeNameHebrew: h,
          loanAmount,
          income,
          outcome,
          id: _id,
          loan: loanId,
          borrower,
        };

        setProposal(objToState);

        setMortgageCycleDetails({
          repaymentTableData,
          calculationData: totalData,
          currentLoans: loans,
        });
      } catch (err) {
        console.error('Error updating mortgage cycle:', err);
        notifications.show({
          title: t('שגיאה'),
          message: t('שגיאה בסיכום הצעה'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };

    calculateMortgageCycleSummary(id);
  }, [id]);

  const maxMonths = Math.max(
    ...(mortgageCycleDetails?.currentLoans?.map(
      (loan) => loan.monthsLeft,
    ) ?? []),
  );

  const details = [
    {
      label: t('pages.mortgageCyclesSummary.label.loanType'),
      value: proposal?.loanTypeNameHebrew,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.borrowerNumber'),
      value: proposal?.borrower?.length,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.loanAmount'),
      value: formatNumberWithCommas(roundNumber(totalLoanAmount)),
      suffix,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.paymentPeriodInMonths'),
      value: maxMonths,
    },
  ];

  const tableHead = [
    t('pages.mortgageCyclesSummary.table.head.month'),
    t('pages.mortgageCyclesSummary.table.head.remaining'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentInterest'),
    t('pages.mortgageCyclesSummary.table.head.totalPayment'),
  ];

  // Use mortgageCycle.repaymentTableData to populate the repayment table
  const tableBody = mortgageCycleDetails?.repaymentTableData?.map((item, index) => [
    item.monthIndex || index + 1, // month number
    `${formatNumberWithCommas(roundNumber(item.total?.remainingPrincipal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.principal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.interest || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.totalPayment || 0))} ₪`,
  ]) || [];

  const disposalboardtableData = {
    head: tableHead,
    body: tableBody,
  };

  if (loading) return <Center><Loader /></Center>;

  return !error ? (
    <>
      <Page
        title={t('pages.summary.title')}
        extraSize="xl"
        hideHelp
        hideNews
      >

        <Page.Head
          title={t('pages.summary.title')}
          subtitle={t('pages.summary.subtitle')}
        />

        <div>
          <DataSummaryCard details={details} />
        </div>

        <div>
          <h3>{t('pages.summary.title.borrowerDetails')}</h3>
          <BorrowerDetailsTable borrowers={proposal.borrower} />
        </div>

        {mortgageCycleDetails?.calculationData && (
          <>
            <Group mb="lg">
              <Text size="lg" fw={600} className={classes.title}>
                {t('pages.mortgageCyclesSummary.totalsComparison.title')}
              </Text>
            </Group>
            <div className={classes.summaryContainer}>
              <TotalsComparison
                totalsBeforeCalculation={mortgageCycleDetails
                  .calculationData.totalsBeforeCalculation}
                totalsAfterCalculation={mortgageCycleDetails
                  .calculationData.totalsAfterCalculation}
              />
              {mortgageCycleDetails?.repaymentTableData && (
                <div className={classes.summaryCollapse}>
                  <Page.Section
                    title={t('pages.mortgageCyclesSummary.title.disposalBoardFor')}
                    withCollapse
                    withBackground
                    startClose
                  >
                    <TableSimple data={disposalboardtableData} />
                  </Page.Section>
                </div>
              )}
            </div>
          </>

        )}

      </Page>
    </>
  ) : <Center>{t('globals.error.title')}</Center>;
};

export default MortgageCycleSummary;
